/**
 * Main entry point for SirmaAI Client Audio SDK
 * Exposes the public API on window.SirmaAI
 */

import { startSession } from './api';
import { connectLiveKit, disconnect } from './livekit';
import { getLogger } from './logger';
import { Room, SessionOverrides } from './types';
import { hideWidget, showWidget, updateWidget } from './ui';
import {
  buildVisualInstructions,
  connectVisualBridge,
  disconnectVisualBridge,
  generateCid,
  onVisualContent,
} from './visual-bridge';

const logger = getLogger('Main');

let room: Room | null = null;
let isRunning = false;

/**
 * Main stream management function
 */
async function startStream(): Promise<void> {
  logger.info('startStream called', { isRunning, hasRoom: !!room });

  // Prevent multiple concurrent streams
  if (isRunning) {
    logger.warn('Stream start prevented: stream already active', { isRunning, hasRoom: !!room });
    throw new Error('Stream already active, cannot start another');
  }

  logger.debug('Setting stream state to active');
  isRunning = true;

  const cleanup = () => {
    logger.debug('Starting cleanup process');
    // Tear down the visual bridge (no-op if it was never opened).
    disconnectVisualBridge();
    hideWidget({
      onRemove: () => {
        logger.debug('Cleanup: resetting room and stream state');
        room = null;
        isRunning = false;
        logger.info('Cleanup complete');
      },
    });
  };

  try {
    logger.debug('Validating SirmaAI configuration');
    // Validate configuration
    if (!window.SirmaAI?.config) {
      logger.error('Configuration validation failed: SirmaAI config not found');
      throw new Error('SirmaAI configuration not found');
    }

    const { apiKey, baseUrl, agentId, microphone, noiseFilter, visualBridgeUrl, visualCatalog } =
      window.SirmaAI.config;
    logger.debug('Configuration extracted successfully', {
      hasApiKey: !!apiKey,
      baseUrl,
      agentId,
      hasMicPreference: !!microphone,
      hasNoiseFilterPreference: !!noiseFilter,
      hasVisualBridge: !!visualBridgeUrl,
      visualCatalogSize: visualCatalog?.length ?? 0,
    });

    // Visual bridge (optional): generate a correlation id for this session and
    // inject it into the agent instructions, so the LLM passes it back to the
    // show_content tool and the bridge can route events to this screen. The
    // same cid is used to register the WebSocket below — keeping both here
    // guarantees they match. The host-provided catalog (from its CMS) tells the
    // LLM the exact keys/kinds it may use.
    const cid = visualBridgeUrl ? generateCid() : null;
    const sessionOverrides: SessionOverrides | undefined = cid
      ? {
          instructions: buildVisualInstructions(cid, visualCatalog),
          instructions_mode: 'append',
        }
      : undefined;

    logger.debug('Showing widget');
    showWidget();

    logger.info('Starting API session request');
    // Start session
    const { token, wsUrl } = await startSession({ apiKey, baseUrl, agentId }, sessionOverrides);
    logger.info('API session started successfully', {
      hasToken: !!token,
      wsUrl: wsUrl ? '[REDACTED]' : null,
    });

    // Open the visual bridge before connecting audio, so it is ready when the
    // agent joins and may trigger a visual. Best-effort: never blocks or fails
    // the audio session.
    if (visualBridgeUrl && cid) {
      connectVisualBridge(visualBridgeUrl, cid);
    }

    logger.info('Connecting to LiveKit');
    // Connect to LiveKit
    room = await connectLiveKit(
      token,
      wsUrl,
      {
        connected: () => {
          logger.info('LiveKit connected successfully, updating widget to streaming state');
          updateWidget('streaming', {
            onStopCallback: () => {
              logger.info('Stop button clicked, ending stream');
              updateWidget('ended');
              if (room) {
                disconnect(room);
              } else {
                logger.warn('Stop clicked but no room exists');
                cleanup();
              }
            },
          });
        },
        disconnected: () => {
          logger.info('LiveKit disconnected');
          updateWidget('ended');
          setTimeout(cleanup, 3000);
        },
        error: (error) => {
          logger.error('LiveKit connection error', { error });
          updateWidget('error');
          setTimeout(cleanup, 5000);
        },
      },
      microphone,
      noiseFilter
    );

    logger.info('startStream completed successfully');
  } catch (error) {
    logger.error('Failed to start stream', {
      error: error instanceof Error ? error.message : error,
      stack: error instanceof Error ? error.stack : undefined,
    });
    updateWidget('error');
    setTimeout(cleanup, 5000);
  }
}

/**
 * Reset the internal state for testing purposes
 * This should only be used in test environments
 */
export function _resetState(): void {
  // This function is only for testing
  room = null;
  isRunning = false;
}

// Export the startStream function for testing
export { startStream };

// Initialize SirmaAI on window
window.SirmaAI = {
  ...window.SirmaAI,
  startStream,
  onVisualContent,
};
